/**
 * useNotificationSettings
 *
 * Owns the notification-settings state for UserSettingsModal: loading and
 * saving the user's settings document, plus all settings state transitions
 * (delegating to the pure functions in ../utils/settingsLogic).
 */

import { useState, useCallback, useMemo } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import {
  useFirebaseStore,
  useToast,
  useLocations,
  type NotificationPageId,
} from '@/shared';
import { logger } from '../../../utils/logger';
import type { NotificationSettings } from '@/types/notification.types';
import {
  DEFAULT_SETTINGS,
  migrateSettings,
  buildAccessibleLocationsByPage,
  getAccessiblePages,
  filterSettingsToAccessible,
  applyMasterToggle,
  applyTogglePage,
  applyToggleLocation,
  applyToggleAllLocations,
  applySetReminderMinutes,
  applyToggleReminderEnabled,
  applyToggleNewEventEnabled,
  applyToggleStatusChangeEnabled,
  applyToggleStatusTransition,
  isPageEnabled as isPageEnabledPure,
  getLocationSummary as getLocationSummaryPure,
  areSettingsEqual,
  type StatusTransition,
} from '../utils/settingsLogic';

export function useNotificationSettings() {
  const { currentUser, db } = useFirebaseStore();
  const { showToast } = useToast();
  const locations = useLocations();

  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);
  // Last loaded/saved settings — "Save Changes" stays disabled until settings differ
  const [savedSettings, setSavedSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);
  const isDirty = useMemo(() => !areSettingsEqual(settings, savedSettings), [settings, savedSettings]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get accessible locations for each page based on permissions
  const accessibleLocationsByPage = useMemo((): Record<NotificationPageId, string[]> => {
    return buildAccessibleLocationsByPage(currentUser, locations.map((l) => l.id));
  }, [currentUser, locations]);

  // Determine which pages the user has access to (non-empty location list)
  const accessiblePages = useMemo((): NotificationPageId[] => {
    return getAccessiblePages(accessibleLocationsByPage);
  }, [accessibleLocationsByPage]);

  const loadSettings = useCallback(async () => {
    if (!currentUser || !db) return;
    setIsLoading(true);
    try {
      const ref = doc(db, 'users', currentUser.email);
      logger.firestore('Read', `users/${currentUser.email} (load settings)`);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        const rawSettings = data.notificationSettings;

        if (rawSettings) {
          // Detect format and migrate if necessary
          const migratedSettings = migrateSettings(rawSettings, accessibleLocationsByPage);

          // Filter page locations to only accessible ones
          const loaded = filterSettingsToAccessible(migratedSettings, accessiblePages, accessibleLocationsByPage);
          setSettings(loaded);
          setSavedSettings(loaded);
        } else {
          // New user - start with defaults (disabled)
          setSettings(DEFAULT_SETTINGS);
          setSavedSettings(DEFAULT_SETTINGS);
        }
      }
    } catch (error) {
      logger.error('Failed to load settings:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, db, accessibleLocationsByPage, accessiblePages, showToast]);

  /**
   * Persist the current settings. Returns true on success so the caller can
   * close the modal.
   */
  const saveSettings = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !db) return false;
    setIsSaving(true);
    try {
      const ref = doc(db, 'users', currentUser.email);
      logger.firestore('Write', `users/${currentUser.email} (save notification settings)`);
      await updateDoc(ref, {
        notificationSettings: settings,
      });
      setSavedSettings(settings);
      showToast('Settings saved successfully', 'success');
      return true;
    } catch (error) {
      logger.error('Failed to save settings:', error);
      showToast('Failed to save settings', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [currentUser, db, settings, showToast]);

  /**
   * Handle toggling the master notification switch (Step 2).
   * Only available after browser permission is granted.
   */
  const handleMasterToggle = useCallback(() => {
    setSettings((prev) => applyMasterToggle(prev, accessiblePages, accessibleLocationsByPage));
  }, [accessiblePages, accessibleLocationsByPage]);

  /**
   * Toggle notifications for a page (simplified - enables both inApp and push)
   */
  const togglePage = useCallback((pageId: NotificationPageId) => {
    setSettings((prev) => applyTogglePage(prev, pageId, accessibleLocationsByPage[pageId]));
  }, [accessibleLocationsByPage]);

  /**
   * Toggle a specific location for a page
   */
  const toggleLocationForPage = useCallback((pageId: NotificationPageId, locationId: string) => {
    setSettings((prev) => applyToggleLocation(prev, pageId, locationId));
  }, []);

  /**
   * Toggle all locations for a page
   */
  const toggleAllLocationsForPage = useCallback((pageId: NotificationPageId) => {
    setSettings((prev) => applyToggleAllLocations(prev, pageId, accessibleLocationsByPage[pageId]));
  }, [accessibleLocationsByPage]);

  /**
   * Set reminder timing for a page (truckingCalendar)
   */
  const setReminderMinutes = useCallback((pageId: NotificationPageId, minutes: number) => {
    setSettings((prev) => applySetReminderMinutes(prev, pageId, minutes));
  }, []);

  /**
   * Toggle reminder notifications for trucking calendar
   */
  const toggleReminderEnabled = useCallback((pageId: NotificationPageId) => {
    setSettings((prev) => applyToggleReminderEnabled(prev, pageId));
  }, []);

  /**
   * Toggle new event notifications for trucking calendar
   */
  const toggleNewEventEnabled = useCallback((pageId: NotificationPageId) => {
    setSettings((prev) => applyToggleNewEventEnabled(prev, pageId));
  }, []);

  /**
   * Toggle status change notifications for trucking calendar
   */
  const toggleStatusChangeEnabled = useCallback((pageId: NotificationPageId) => {
    setSettings((prev) => applyToggleStatusChangeEnabled(prev, pageId));
  }, []);

  /**
   * Toggle a specific status transition setting for trucking calendar
   */
  const toggleStatusTransition = useCallback(
    (pageId: NotificationPageId, transition: StatusTransition) => {
      setSettings((prev) => applyToggleStatusTransition(prev, pageId, transition));
    },
    []
  );

  /**
   * Check if a page has notifications enabled
   */
  const isPageEnabled = useCallback((pageId: NotificationPageId): boolean => {
    return isPageEnabledPure(settings, pageId);
  }, [settings]);

  /**
   * Get location summary text for a page
   */
  const getLocationSummary = useCallback((pageId: NotificationPageId): string => {
    return getLocationSummaryPure(settings, pageId, accessibleLocationsByPage[pageId]);
  }, [settings, accessibleLocationsByPage]);

  return {
    settings,
    isLoading,
    isSaving,
    isDirty,
    accessibleLocationsByPage,
    accessiblePages,
    loadSettings,
    saveSettings,
    handleMasterToggle,
    togglePage,
    toggleLocationForPage,
    toggleAllLocationsForPage,
    setReminderMinutes,
    toggleReminderEnabled,
    toggleNewEventEnabled,
    toggleStatusChangeEnabled,
    toggleStatusTransition,
    isPageEnabled,
    getLocationSummary,
  };
}
